import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
// import { fr } from 'date-fns/locale';
import { ArrowLeft, Star, MapPin, Calendar, CheckCircle, Smile, Users, Shield, Car, Hotel, Activity, Map } from 'lucide-react';
import circuitService from '../services/circuitService';
import cmiService from '../services/cmiService';  
import toast from 'react-hot-toast';
import { notifyApiError } from "../utils/apiError";
import translations from '../contexts/translations';
import { useAuth } from "../contexts/AuthContext";
import { 
  ModernInput, 
  ModernSelect, 
  ModernDatePicker, 
  ModernCheckbox 
} from "../components/ui/FormComponents";
import { Send, Mail, User, Globe, MessageSquare, Phone } from 'lucide-react';
import Seo, { SITE_URL, cleanDescription } from '../components/Seo';

const CircuitDetail = ({ language }) => {
  const { currentUser } = useAuth();
  const t = translations[language];
  const { id } = useParams();
  const navigate = useNavigate();
  const [circuit, setCircuit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [reservationData, setReservationData] = useState({
    dateDebut: '',
    nombrePersonnes: 1,
    informationsVoyageurs: [{ nom: '', prenom: '', dateNaissance: '', nationalite: '' }],
    commentaires: '',
    email: '',
    telephone: '',
    adresse: ''
  });
  // const [reservationSuccess, setReservationSuccess] = useState(false);
  const [acceptCgv, setAcceptCgv] = useState(false);

  const loadCircuitDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await circuitService.getCircuitById(id);
      setCircuit(data);
      setActiveImage(data.image || '');
      setLoading(false);
    } catch (err) {
      setError(t.errorFetchingCircuit || 'Error loading circuit details');
      toast.error(t.errorFetchingCircuit || 'Error loading circuit details');
      setLoading(false);
    }
  }, [id, t.errorFetchingCircuit]);

  useEffect(() => {
    localStorage.getItem('token');
    // setIsAuthenticated(!!token);
    loadCircuitDetails();
  }, [id, loadCircuitDetails]);

  const handleImageClick = (image) => setActiveImage(image);

  const handleReservationClick = () => {
    setAcceptCgv(false);
    setShowReservationForm(true);
  };



  const handlePersonnesChange = (e) => {
    const nombrePersonnes = parseInt(e.target.value) || 1;
    setReservationData((prev) => {
      const newVoyageurs = [...prev.informationsVoyageurs];
      if (nombrePersonnes > prev.informationsVoyageurs.length) {
        for (let i = prev.informationsVoyageurs.length; i < nombrePersonnes; i++) {
          newVoyageurs.push({
            nom: '',
            prenom: '',
            dateNaissance: '',
            nationalite: '',
          });
        }
      }
      return {
        ...prev,
        nombrePersonnes,
        informationsVoyageurs: newVoyageurs.slice(0, nombrePersonnes),
      };
    });
  };

  const handleVoyageurChange = (index, field, value) => {
    setReservationData((prev) => {
      const newVoyageurs = [...prev.informationsVoyageurs];
      newVoyageurs[index] = { ...newVoyageurs[index], [field]: value };
      return { ...prev, informationsVoyageurs: newVoyageurs };
    });
  };

  const handleSubmitReservation = async (e) => {
  e.preventDefault();
  if (!acceptCgv) {
    toast.error(t.mustAcceptCgv || "Veuillez accepter les CGV avant de continuer vers le paiement.");
    return;
  }

  try {
    // 1) Vérifs de base
    if (!reservationData.dateDebut) {
      toast.error(t.startDateRequired || "Veuillez choisir une date de départ");
      return;
    }

    if (!reservationData.email && !currentUser?.email) {
      toast.error(t.emailRequired || "Veuillez saisir votre adresse e-mail");
      return;
    }

    const nombrePersonnes = Number(reservationData.nombrePersonnes || 1);
    const prixTotal = Number(circuit.prix || 0) * nombrePersonnes;

    if (!Number.isFinite(prixTotal) || prixTotal <= 0) {
      toast.error("Montant invalide");
      return;
    }

    // 2) Construire un nom d'utilisateur lisible pour CMI
    const firstTraveller = reservationData.informationsVoyageurs?.[0];
    const travellerName =
      firstTraveller &&
      (firstTraveller.prenom || firstTraveller.nom)
        ? `${firstTraveller.prenom || ""} ${firstTraveller.nom || ""}`.trim()
        : "";

    const utilisateur =
      travellerName ||
      (currentUser?.prenom || currentUser?.nom
        ? `${currentUser?.prenom || ""} ${currentUser?.nom || ""}`.trim()
        : currentUser?.email || reservationData.email);

    const email = currentUser?.email || reservationData.email;

    toast.loading(
      t.processingReservation || "Préparation de votre réservation...",
      { id: "cmi-circuit" }
    );

    // 3) Payload attendu par /api/cmi/circuit/create
    const payload = {
      circuitId: circuit._id,
      dateDepart: reservationData.dateDebut,
      nbAdultes: nombrePersonnes,     // pour l'instant tout le monde = adulte
      nbEnfants: 0,                   // tu adapteras plus tard si tu ajoutes un champ enfants
      utilisateur,
      email,
      // ✅ Infos client (requises pour créer la réservation après paiement)
      fullName: utilisateur || `${currentUser?.prenom || ""} ${currentUser?.nom || ""}`.trim(),
      phone: reservationData.telephone || currentUser?.telephone || "",
      address: reservationData.adresse || "",
      commentaires: reservationData.commentaires || "",
      lang: (language || "fr").slice(0, 2).toLowerCase(),
      prixTotal,                      // ✅ très important : c’est ce montant qui va à CMI
      // debug: 1,                    // décommente pour voir le JSON au lieu de l’HTML
    };

    // 4) Appel au backend CMI → renvoie du HTML (form + auto-submit)
    const html = await cmiService.createCircuitPayment(payload);

    toast.success(
      t.redirectingToPayment || "Redirection vers la page de paiement...",
      { id: "cmi-circuit", duration: 1500 }
    );

    // 5) Injecter l’HTML CMI dans la page (même logique que pour les hôtels)
    setTimeout(() => {
      document.open();
      document.write(html);
      document.close();
    }, 600);
  } catch (err) {
    console.error("Erreur CMI circuit:", err);
    const msgs = notifyApiError(err, t.reservationError || "Erreur lors de la réservation", { id: "cmi-circuit" });
    setError(msgs[0]);
  }
};


  const renderStars = (note) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${i < note ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const bookingSteps = [
    {
      title: t.selectDates || 'Select Dates',
      description: t.selectDatesDesc || 'Choose your departure date for the circuit.',
      icon: <Calendar className="text-3xl" style={{ color: '#F97316' }} />,
    },
    {
      title: t.book || 'Book',
      description: t.bookDesc || 'Secure your booking with our seamless process and flexible payment options.',
      icon: <CheckCircle className="text-3xl" style={{ color: '#F97316' }} />,
    },
    {
      title: t.enjoy || 'Enjoy',
      description: t.enjoyDesc || 'Experience Morocco’s magic with confidence, supported by our 24/7 local team.',
      icon: <Smile className="text-3xl" style={{ color: '#F97316' }} />,
    },
  ];

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="text-amber-900">{t.loading || 'Loading...'}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl max-w-md">
          {error}
          <button
            onClick={() => navigate('/circuit')}
            className="mt-4 w-full py-2 px-4 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
          >
            {t.backToCircuits || 'Back to circuits'}
          </button>
        </div>
      </div>
    );
  }

  if (!circuit) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-6 py-4 rounded-xl max-w-md">
          {t.circuitNotFound || 'Circuit not found'}
          <button
            onClick={() => navigate('/circuit')}
            className="mt-4 w-full py-2 px-4 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
          >
            {t.backToCircuits || 'Back to circuits'}
          </button>
        </div>
      </div>
    );
  }

  const circuitPath = `/circuit/${id}`;
  const circuitCanonical = `${SITE_URL}${circuitPath}`;
  const circuitImages = [circuit.image, ...(circuit.galerie || [])].filter(Boolean);
  const reviewCount = Array.isArray(circuit.avis) ? circuit.avis.length : 0;
  const circuitJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: circuit.titre,
      description: cleanDescription(circuit.description, 280),
      ...(circuitImages.length ? { image: circuitImages } : {}),
      brand: { "@type": "Brand", name: "Nouzha" },
      category: "Circuit touristique au Maroc",
      ...(circuit.prix
        ? {
            offers: {
              "@type": "Offer",
              price: String(circuit.prix),
              priceCurrency: "MAD",
              availability: "https://schema.org/InStock",
              url: circuitCanonical,
              seller: { "@type": "TravelAgency", name: "Nouzha", url: `${SITE_URL}/` },
            },
          }
        : {}),
      ...(circuit.note && reviewCount > 0
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(circuit.note),
              reviewCount: String(reviewCount),
              bestRating: "5",
            },
          }
        : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Circuits", item: `${SITE_URL}/circuit` },
        { "@type": "ListItem", position: 3, name: circuit.titre, item: circuitCanonical },
      ],
    },
  ];

  return (
    <div
      className="min-h-screen py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
    >
      <Seo
        path={circuitPath}
        title={circuit.titre}
        description={cleanDescription(
          circuit.description ||
            `Circuit ${circuit.titre} au Maroc avec Nouzha : itinéraire d'exception, guide expert et transport privé.`
        )}
        keywords={`circuit Maroc, ${circuit.titre}, voyage organisé Maroc, ${(circuit.villes || []).join(", ")}`}
        image={circuit.image || undefined}
        type="article"
        jsonLd={circuitJsonLd}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/src/assets/zelij3.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '400px',
          backgroundAttachment: 'fixed',
          opacity: 0.08,
        }}
      ></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]"></div>
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* {reservationSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-xl mb-8"
          >
            {t.reservationSuccess || 'Reservation successful! Redirecting to your reservations...'}
          </motion.div>
        )} */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-amber-100">
            <motion.button
              onClick={() => navigate('/circuit')}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center text-orange-600 hover:text-orange-700 mb-4 font-semibold transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t.backToCircuits || 'Back to circuits'}
            </motion.button>
            <h1 className="text-3xl font-serif font-extrabold text-amber-900">{circuit.titre}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-amber-900/80">
              <div className="flex items-center">{renderStars(circuit.note)}</div>
              <span>({circuit.avis || 0} {t.reviews || 'reviews'})</span>
              <span className="text-amber-900/80">•</span>
              <div className="flex items-center">
                <MapPin className="h-5 w-5 mr-1 text-orange-600" />
                {circuit.villes?.join(', ') || 'N/A'}
              </div>
              <span className="text-amber-900/80">•</span>
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-1 text-orange-600" />
                {circuit.duree || 'N/A'}
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="p-6 bg-orange-50/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <img
                  src={activeImage || circuit.image || '/placeholder-circuit.jpg'}
                  alt={circuit.titre}
                  className="w-full h-96 object-cover rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 h-96 overflow-y-auto">
                {circuit.image && (
                  <img
                    src={circuit.image}
                    alt="Main"
                    className={`w-full h-32 object-cover rounded-lg cursor-pointer ${activeImage === circuit.image ? 'ring-2 ring-orange-500' : ''}`}
                    onClick={() => handleImageClick(circuit.image)}
                  />
                )}
                {circuit.galerie?.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${circuit.titre} - ${index + 1}`}
                    className={`w-full h-32 object-cover rounded-lg cursor-pointer ${activeImage === img ? 'ring-2 ring-orange-500' : ''}`}
                    onClick={() => handleImageClick(img)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Details and Reservation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6">
            <div className="md:col-span-2 space-y-8">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-serif font-semibold text-orange-600 mb-4">
                  {t.aboutCircuit || 'About this Circuit'}
                </h2>
                <div className="text-amber-900/80 leading-relaxed space-y-4">
                  {circuit.description?.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph || t.noDescription || 'No description available.'}</p>
                  ))}
                </div>
              </div>

              {/* Highlights */}
              {circuit.highlights?.length > 0 && (
                <div>
                  <h2 className="text-2xl font-serif font-semibold text-orange-600 mb-4">
                    {t.highlights || 'Highlights'}
                  </h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {circuit.highlights.map((point, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-2" />
                        <span className="text-amber-900/80">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Included Services */}
              {circuit.inclus && (
                <div>
                  <h2 className="text-2xl font-serif font-semibold text-orange-600 mb-4">
                    {t.included || 'What’s Included'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {circuit.inclus.transport && (
                      <div className="flex">
                        <div className="bg-orange-100 rounded-full p-3 mr-4">
                          <Car className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-amber-900 mb-2">{t.transport || 'Transport'}</h3>
                          <p className="text-amber-900/80">{circuit.inclus.transport}</p>
                        </div>
                      </div>
                    )}
                    {circuit.inclus.hebergement && (
                      <div className="flex">
                        <div className="bg-orange-100 rounded-full p-3 mr-4">
                          <Hotel className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-amber-900 mb-2">{t.accommodation || 'Accommodation'}</h3>
                          <p className="text-amber-900/80">{circuit.inclus.hebergement}</p>
                        </div>
                      </div>
                    )}
                    {circuit.inclus.visite && (
                      <div className="flex">
                        <div className="bg-orange-100 rounded-full p-3 mr-4">
                          <Map className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-amber-900 mb-2">{t.guidedTour || 'Guided Tour'}</h3>
                          <p className="text-amber-900/80">{circuit.inclus.visite}</p>
                        </div>
                      </div>
                    )}
                    {circuit.inclus.activites && (
                      <div className="flex">
                        <div className="bg-orange-100 rounded-full p-3 mr-4">
                          <Activity className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-amber-900 mb-2">{t.activities || 'Activities'}</h3>
                          <p className="text-amber-900/80">{circuit.inclus.activites}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Reservation Card */}
            <div>
              <div className="bg-white/90 border border-amber-100 rounded-3xl p-6 shadow-lg sticky top-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-3xl font-bold text-orange-600">{circuit.prix || 'N/A'} €</div>
                  <div className="text-sm text-amber-900/80">{t.perPerson || 'per person'}</div>
                </div>

                {!showReservationForm ? (
                  <motion.button
                    onClick={handleReservationClick}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-5 px-6 bg-gray-900 text-white rounded-[2rem] font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-2xl"
                  >
                    {t.bookNow || 'Réserver maintenant'}
                  </motion.button>
                ) : (
                  <form onSubmit={handleSubmitReservation} className="space-y-8">
                    <div className="space-y-6">
                      <ModernDatePicker
                        label={t.startDate || 'Date de départ'}
                        value={reservationData.dateDebut}
                        onChange={(e) => setReservationData(prev => ({ ...prev, dateDebut: e.target.value }))}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        language={language}
                        required
                      />
                      
                      {!currentUser?.email && (
                        <ModernInput
                          label={t.email || 'Email'}
                          icon={Mail}
                          type="email"
                          value={reservationData.email}
                          onChange={(e) => setReservationData(prev => ({ ...prev, email: e.target.value }))}
                          required
                          placeholder="votre@email.com"
                        />
                      )}

                      <ModernInput
                        label={t.phone || 'Téléphone'}
                        icon={Phone}
                        type="tel"
                        value={reservationData.telephone}
                        onChange={(e) => setReservationData(prev => ({ ...prev, telephone: e.target.value }))}
                        required
                        placeholder="06 00 00 00 00"
                      />

                      <ModernInput
                        label={t.address || 'Adresse'}
                        icon={MapPin}
                        type="text"
                        value={reservationData.adresse}
                        onChange={(e) => setReservationData(prev => ({ ...prev, adresse: e.target.value }))}
                        required
                        placeholder="Adresse complète"
                      />

                      <ModernSelect
                        label={t.numberOfPeople || 'Nombre de personnes'}
                        icon={Users}
                        value={reservationData.nombrePersonnes}
                        onChange={handlePersonnesChange}
                        required
                        options={Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))}
                      />
                    </div>

                    <div className="pt-6 border-t border-amber-100 space-y-6">
                      <h3 className="text-xl font-black text-amber-900 uppercase tracking-tighter flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white">
                          <Users size={16} />
                        </div>
                        {t.travelerInfo || 'Infos Voyageurs'}
                      </h3>
                      
                      {reservationData.informationsVoyageurs.map((voyageur, index) => (
                        <div key={index} className="p-6 rounded-[2rem] bg-orange-50/50 border border-orange-100 space-y-6 relative overflow-hidden group">
                          <div className="absolute top-0 left-0 w-1 h-full bg-orange-500 opacity-30 group-hover:opacity-100 transition-opacity" />
                          <h4 className="font-black text-xs text-orange-600 uppercase tracking-widest flex items-center gap-2">
                             {t.traveler || 'Voyageur'} {index + 1}
                          </h4>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ModernInput
                              label={t.lastName || 'Nom'}
                              icon={User}
                              value={voyageur.nom}
                              onChange={(e) => handleVoyageurChange(index, 'nom', e.target.value)}
                              required
                            />
                            <ModernInput
                              label={t.firstName || 'Prénom'}
                              icon={User}
                              value={voyageur.prenom}
                              onChange={(e) => handleVoyageurChange(index, 'prenom', e.target.value)}
                              required
                            />
                            <ModernDatePicker
                              label={t.dateOfBirth || 'Date de naissance'}
                              value={voyageur.dateNaissance}
                              onChange={(e) => handleVoyageurChange(index, 'dateNaissance', e.target.value)}
                              required
                              language={language}
                            />
                            <ModernInput
                              label={t.nationality || 'Nationalité'}
                              icon={Globe}
                              value={voyageur.nationalite}
                              onChange={(e) => handleVoyageurChange(index, 'nationalite', e.target.value)}
                              required
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <ModernInput
                      label={t.comments || 'Commentaires / Demandes spéciales'}
                      icon={MessageSquare}
                      value={reservationData.commentaires}
                      onChange={(e) => setReservationData(prev => ({ ...prev, commentaires: e.target.value }))}
                      multiline
                      rows={4}
                    />

                    <div className="p-6 bg-amber-50 rounded-[2rem] border border-amber-100">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-amber-800 uppercase tracking-tighter opacity-60">{t.pricePerPerson || 'Prix par personne'}</span>
                        <span className="font-black text-amber-900">{circuit.prix || 'N/A'} €</span>
                      </div>
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-bold text-amber-800 uppercase tracking-tighter opacity-60">{t.numberOfPeople || 'Nombre de personnes'}</span>
                        <span className="font-black text-amber-900">x {reservationData.nombrePersonnes}</span>
                      </div>
                      <div className="flex justify-between items-center pt-4 border-t border-amber-200">
                        <span className="text-lg font-black text-amber-900 uppercase tracking-tighter">{t.total || 'TOTAL'}</span>
                        <span className="text-2xl font-black text-orange-600">{(circuit.prix * reservationData.nombrePersonnes) || 'N/A'} €</span>
                      </div>
                    </div>

                      <div className="space-y-6">
                        <ModernCheckbox
                          checked={acceptCgv}
                          onChange={(e) => setAcceptCgv(e.target.checked)}
                        >
                          {t.acceptTermsPrefix || "J’ai lu et j’accepte les"}{" "}
                          <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 font-bold hover:underline">
                            {t.cgvLinkLabel || "Conditions Générales de Vente"}
                          </a>{" "}
                          ainsi que les conditions de paiement via CMI.
                        </ModernCheckbox>

                        <div className="flex flex-wrap items-center gap-3 pl-9">
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t.securePaymentLabel || "Paiement sécurisé :"}</span>
                          <a href="/paiement-securise" target="_blank" rel="noreferrer" className="text-xs text-orange-600 font-bold hover:underline">
                            Voir les informations CMI / Visa / MasterCard / 3DS
                          </a>
                        </div>
                      </div>

                    <div className="space-y-4 pt-4">
                      <motion.button
                        whileHover={{ scale: acceptCgv ? 1.02 : 1 }}
                        whileTap={{ scale: acceptCgv ? 0.98 : 1 }}
                        type="submit"
                        disabled={!acceptCgv}
                        className={`w-full py-5 px-6 rounded-[2rem] font-black uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 transition-all ${
                          acceptCgv ? 'bg-gray-900 text-white hover:bg-orange-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Send size={20} />
                        {t.confirmReservation || 'Confirmer la réservation'}
                      </motion.button>

                      <button
                        type="button"
                        onClick={() => setShowReservationForm(false)}
                        className="w-full py-4 text-sm font-black text-amber-900 uppercase tracking-widest hover:text-orange-600 transition-colors"
                      >
                        {t.cancel || 'Annuler'}
                      </button>
                    </div>
                  </form>
                )}

                <div className="mt-6 text-sm text-amber-900/80 space-y-2">
                  <p className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-orange-600" />
                    {t.availableYearRound || 'Available year-round'}
                  </p>
                  <p className="flex items-center">
                    <Users className="h-5 w-5 mr-2 text-orange-600" />
                    {t.idealFor || 'Ideal for'}: {circuit.participants || t.allTravelers || 'All types of travelers'}
                  </p>
                  <p className="flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-orange-600" />
                    {t.freeCancellation || 'Free cancellation up to 7 days before departure'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Booking Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
              {t.bookingProcessTitle || 'Booking Process'}
            </span>
            <h2 className="text-3xl font-serif font-extrabold mb-4 text-amber-900">
              {t.bookingProcess || 'Plan Your Circuit with Ease'}
            </h2>
            <p className="text-lg max-w-2xl mx-auto leading-relaxed text-amber-900/80">
              {t.bookingProcessDesc || 'Follow these simple steps to book your perfect Moroccan adventure.'}
            </p>
          </div>

          <div className="relative">
            <div
              className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 z-0"
              style={{ background: 'linear-gradient(to bottom, rgba(249, 115, 22, 0.5), #F97316, rgba(249, 115, 22, 0.5))' }}
            ></div>
            <div className="relative z-10 space-y-24">
              {bookingSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex flex-col md:flex-row items-center relative"
                >
                  {index % 2 === 0 ? (
                    <>
                      <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-right">
                        <h3 className="text-2xl font-serif font-semibold mb-3 text-amber-900">
                          {step.title}
                        </h3>
                        <p className="leading-relaxed text-amber-900/80">
                          {step.description}
                        </p>
                      </div>
                      <div
                        className="backdrop-blur-md rounded-full p-5 z-10 shadow-xl border border-amber-100"
                        style={{ backgroundColor: 'rgba(255, 247, 237, 0.9)', color: '#F97316' }}
                      >
                        {step.icon}
                      </div>
                      <div className="md:w-1/2 md:pl-16 hidden md:block"></div>
                    </>
                  ) : (
                    <>
                      <div className="md:w-1/2 md:pr-16 hidden md:block"></div>
                      <div
                        className="backdrop-blur-md rounded-full p-5 z-10 shadow-xl border border-amber-100"
                        style={{ backgroundColor: 'rgba(255, 247, 237, 0.9)', color: '#F97316' }}
                      >
                        {step.icon}
                      </div>
                      <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-left">
                        <h3 className="text-2xl font-serif font-semibold mb-3 text-amber-900">
                          {step.title}
                        </h3>
                        <p className="leading-relaxed text-amber-900/80">
                          {step.description}
                        </p>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CircuitDetail;